/**
 * Bot detection helpers. The honeypot detector looks for any input whose
 * `name` matches /^_honeypot$/i and returns true when any such input is
 * non-empty. The timing helpers compare the first-interaction timestamp
 * (set by createPageLoadTimer on the first pointerdown / keydown / scroll
 * after script load) to the current submit time and flag any submit that
 * lands inside the 2-second threshold as suspected automation. A null
 * first-interaction timestamp -- meaning no interaction occurred at all --
 * is itself treated as suspicious.
 */

export const BOT_TIMING_THRESHOLD_MS = 2000;

const HONEYPOT_NAME_REGEX = /^_honeypot$/i;

/**
 * True when any input on the form has a honeypot name AND a non-empty
 * value. Bots that auto-fill all fields will populate the hidden honeypot.
 */
export function isHoneypotTriggered(form: HTMLFormElement): boolean {
  try {
    const inputs = form.querySelectorAll('input, textarea');
    for (let i = 0; i < inputs.length; i++) {
      const el = inputs[i] as HTMLInputElement | HTMLTextAreaElement;
      const name = el.name || '';
      if (!HONEYPOT_NAME_REGEX.test(name)) continue;
      const value = (el.value || '').trim();
      if (value.length > 0) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * True when the submit happened sooner than BOT_TIMING_THRESHOLD_MS after
 * the first user interaction was recorded. The `pageLoadTime` parameter is
 * the first-interaction timestamp (despite the name -- preserved for the
 * caller's existing variable naming). A null value means no interaction
 * was ever recorded, which itself is suspicious so we return true.
 */
export function isBotSuspectedByTiming(pageLoadTime: number | null, submitTime: number): boolean {
  if (pageLoadTime === null) return true;
  return submitTime - pageLoadTime < BOT_TIMING_THRESHOLD_MS;
}

/**
 * Wire up first-interaction listeners that capture the timestamp of the
 * first real user gesture. Returns a getter so submit handlers can read
 * the value at submit time.
 */
export function createPageLoadTimer(): () => number | null {
  let pageLoadTime: number | null = null;
  if (typeof document === 'undefined') return () => pageLoadTime;
  const mark = (): void => {
    if (pageLoadTime === null) pageLoadTime = Date.now();
  };
  try {
    document.addEventListener('pointerdown', mark, { once: true, passive: true });
    document.addEventListener('keydown', mark, { once: true, passive: true });
    document.addEventListener('scroll', mark, { once: true, passive: true });
  } catch {
    /* listeners optional */
  }
  return () => pageLoadTime;
}
